import { getSubIdFromUrlPath } from '@utils/sub-id-routing.utils';
import { useSegmentFlagOverride } from '@utils/segment-flag.utils';
import { getActiveSubscription } from '@/data/session/session-globals';
import { Flags } from './types';
import isObject from 'lodash/isObject';

/* Notes:
- This data is set on load and is NON-REACTIVE outside of Vue
- Leave all Vue references out of this file. Do not import anything that might also import Vue or Pinia.
- Because local storage is extra exposed, avoid using these flags for core gating functionality like Vue
  route guards. Use Pinia.
- Do not pollute this local store with any UI-specific data (use Pinia) or PII (no user data)
- Use for commonly accessed static data like: sub flags, app flags, etc.
*/

const localFlags: Record<string, Flags> = {};
let activeSubId: string | undefined;

// Storage keys
export const SEGMENT_FLAG_STORAGE_KEY = 'p_segment_flags';

export const SUBSCRIPTION_FLAG_STORAGE_KEY = 'p_subscription_flags';

export const PRODUCT_LINE_STORAGE_KEY = 'p_product_lines';

// Flags keys that should be cleaned out of local storage on logout
const flagsToClear = [SEGMENT_FLAG_STORAGE_KEY, SUBSCRIPTION_FLAG_STORAGE_KEY, PRODUCT_LINE_STORAGE_KEY];

/* Getter/setters */
export function setSegmentFlags (update: string[]): void {
    setFlags(update, SEGMENT_FLAG_STORAGE_KEY);
}

export function hasSegmentFlag (key: string): boolean {
    if (useSegmentFlagOverride()) {
        return true;
    }

    return hasFlag(key, SEGMENT_FLAG_STORAGE_KEY);
}

export function localSegmentFlagsExist (): boolean {
    return localFlagsExist(SEGMENT_FLAG_STORAGE_KEY);
}

export function setSubscriptionFlags (update: string[], subId?: string): void {
    const storageKey = formatSubIdKey(SUBSCRIPTION_FLAG_STORAGE_KEY, subId);
    setFlags(update, storageKey);
}

export function hasSubscriptionFlag (key: string, subId?: string): boolean {
    const storageKey = formatSubIdKey(SUBSCRIPTION_FLAG_STORAGE_KEY, subId);

    return hasFlag(key, storageKey);
}

export function localSubscriptionFlagsExist (subId?: string): boolean {
    const storageKey = formatSubIdKey(SUBSCRIPTION_FLAG_STORAGE_KEY, subId);

    return localFlagsExist(storageKey);
}

export function clearFlags (storageKey?: string): void {
    if (storageKey) {
        localStorage.removeItem(storageKey);
        delete localFlags[storageKey];
    } else {
        flagsToClear.forEach((flag) => {
            Object.keys(localStorage).forEach((localKey) => {
                if (localKey.startsWith(flag)) {
                    clearFlags(localKey);
                }
            });
        });
    }
}

/* Internal utils */
function setFlags (update: string[] | Flags, storageKey: string): void {
    let newFlags: Flags = {};

    if (!update) {
        return;
    }

    if (Array.isArray(update)) {
        update.forEach((flag) => {
            newFlags[flag] = true;
        });
    } else if (isObject(update)) {
        newFlags = update;
    } else {
        throw Error(`Update for ${storageKey} is in the wrong format.`);
    }

    localStorage.setItem(storageKey, btoa(JSON.stringify(newFlags)));
    localFlags[storageKey] = newFlags;
}

function hasFlag (flag: string, storageKey: string): boolean {
    const flags = ensureFlags(storageKey);

    if (!flags) {
        return false;
    }

    return flags[flag] || false;
}

function localFlagsExist (storageKey: string): boolean {
    if (localFlags[storageKey]) {
        return true;
    }

    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const localFlags: any = localStorage.getItem(storageKey);

        return !!localFlags;
    } catch {
        return false;
    }
}

// Read flags from a localStorage cache until the latest values are received.
function ensureFlags (storageKey: string): Flags {
    if (!localFlags[storageKey]) {
        let parsedFlags: Flags;

        try {
            parsedFlags = JSON.parse(atob(localStorage.getItem(storageKey) || '{}'));
        } catch {
            parsedFlags = {};
        }

        localFlags[storageKey] = parsedFlags;
    }

    return localFlags[storageKey];
}

function confirmSubId (subId?: string): string | undefined {
    if (!subId) {
        if (!activeSubId) {
            activeSubId = getActiveSubscription()?.id;
        }

        subId = activeSubId || getSubIdFromUrlPath();

        if (!subId && window.Cypress) {
            subId = 'testSub';
        }
    }

    return subId;
}

function formatSubIdKey (key: string, subId?: string): string {
    if (key) {
        subId = confirmSubId(subId);

        if (!subId) {
            throw new Error(`Cannot store data under ${key} with missing sub ID.`);
        }

        if (!key.includes(subId)) {
            return `${key}_${subId}`;
        }
    }

    return key;
}

declare global {
    interface Window {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        Cypress?: any;
    }
}
